import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import AccountAvatar from "@/components/common/AccountAvatar";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";
import { MUM_HUB_WEB_ENABLED } from "@/lib/mum-hub/flag";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [location] = useLocation();
  const { customer } = useAuth();

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Shop", href: "/shop" },
    { name: "Style Hub", href: "/style-hub" },
    // MAR-219. The STATIC half of the gate only (build flag or the browser
    // override), never the entitlement read: an entry that arrives a beat
    // after first paint pushes every link beside it and the whole page with
    // them, which Adrian recorded as "a weird jump of the whole page". A
    // gated customer without the flag reaches Mum Hub from My Hub instead.
    // Placed after Style Hub because it is the other editorial destination.
    ...(MUM_HUB_WEB_ENABLED ? [{ name: "Mum Hub", href: "/mum-hub" }] : []),
    { name: "About", href: "/about" },
    { name: "How I Choose", href: "/how-i-choose" },
    { name: "Personal Stylist Services", href: "https://www.mariapersonalshopper.com/personal-shopper-london", external: true },
  ];

  // /my-hub redirects guests to /account/login, so the header stays visually
  // stable regardless of session state (or whether the customer is offline).
  const accountLabel = customer
    ? `My hub, signed in as ${customer.email}`
    : "My hub, sign in";

  useEffect(() => {
    // Start at 0 instead of reading window.scrollY at mount. Reading layout
    // properties during the React effect phase forces a reflow because other
    // mounting components may have just invalidated layout. The first scroll
    // event will set a real value before any comparison matters.
    let lastScrollY = 0;
    let ticking = false;

    const update = () => {
      ticking = false;

      // Batch all layout reads first so they happen against a clean layout.
      const currentScrollY = window.scrollY;
      const isMobile = window.innerWidth < 1024;
      const filterBar = document.getElementById("mobile-filter-bar");
      const filterBarTop = filterBar ? filterBar.getBoundingClientRect().top : null;

      // Decide new state without touching the DOM.
      let hidden = false;
      if (isMobile) {
        if (filterBarTop !== null) {
          hidden = filterBarTop <= 64;
        } else {
          hidden = currentScrollY > lastScrollY && currentScrollY > 50;
        }
      }

      lastScrollY = currentScrollY;

      // Batch all writes at the end so they invalidate layout once, after
      // every read has already happened.
      setIsHidden(hidden);
      document.documentElement.style.setProperty("--header-height", hidden ? "0px" : "4rem");
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`bg-warm-white/95 dark:bg-gray-900/95 backdrop-blur-sm sticky top-0 z-40 border-b border-light-taupe/20 dark:border-gray-700/20 transform transition-transform duration-300 ${isHidden ? "-translate-y-full" : "translate-y-0"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="min-w-0">
            <Link href="/" className="flex items-center">
              <img src="/logo-horizontal.svg" alt="Capsule Chic Hub by Maria Pastor" width={440} height={68} className="h-10 md:h-12 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="ml-6 xl:ml-10 flex items-baseline space-x-5 xl:space-x-7">
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-soft-blush transition-colors duration-200 font-medium whitespace-nowrap"
                    onClick={() => trackEvent("click_personal_stylist", { link_url: item.href })}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`text-sm hover:text-soft-blush transition-colors duration-200 font-medium whitespace-nowrap ${
                      location === item.href ? "text-soft-blush" : ""
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* Right-side actions */}
          <div className="flex items-center gap-2">
            <Link
              href="/my-hub"
              aria-label={accountLabel}
              className="hidden lg:inline-flex items-center gap-[10px] py-1 pl-1 pr-2 rounded-full hover:bg-light-taupe/30 transition-colors duration-200"
            >
              <AccountAvatar customer={customer} />
              <span className="text-sm font-medium text-charcoal whitespace-nowrap">
                My hub
              </span>
            </Link>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-light-taupe/20 dark:border-gray-700/20">
              <Link
                href="/my-hub"
                aria-label={accountLabel}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-[10px] px-3 py-2 rounded-md hover:bg-light-taupe/30 transition-colors duration-200"
              >
                <AccountAvatar customer={customer} />
                <span className="text-sm font-medium text-charcoal">My hub</span>
              </Link>
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 hover:text-soft-blush transition-colors duration-200 font-medium"
                    onClick={() => {
                      trackEvent("click_personal_stylist", { link_url: item.href });
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`block px-3 py-2 hover:text-soft-blush transition-colors duration-200 font-medium ${
                      location === item.href ? "text-soft-blush" : ""
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
